import { tWithKeyAttr as t } from "../../i18nHelper";
import React from "react";
import ListPagination from "../listPagination/listPagination";
import Loader from "../loader/loader";
import styles from "../pendingTreatmentPlans/pendingTreatmentPlans.module.css";
import stylesCompanyInquiries from "./companyInquiries.module.css";
import { useAppSelector } from "../../services/hooks";
import { adminSelector } from "../../services/selectors/user.selectors";
import { useGetMainPageInfoQuery } from "../../services/api/user.api";
import { TTakenByOthers, TUsersWithOutTherapist } from "../../services/types/auth.types";
import ExportTableButtons from "../exportTableButtons/ExportTableButtons";
import eyeIcon from "../../images/eye_icon.svg";
import { getPrintDate } from "../../utils/utils";
import InquiryDocument from "../inquiryDocument/inquiryDocument";
import { useGetStatusesQuery } from "../../services/api/utils.api";

type TabState = "myInquiries" | "usersWithOutTherapist" | "takenByOthers";

type MainPageInquiryRow = TUsersWithOutTherapist | TTakenByOthers;

const ITEMS_PER_PAGE = 10;

const TAB_CONFIG = {
  myInquiries: { label: "ADMIN_HOME_TAB_MY_INQUIRIES", dataKey: "myInquiries" },
  usersWithOutTherapist: {
    label: "ADMIN_HOME_TAB_UNASSIGNED_INQUIRIES",
    dataKey: "usersWithOutTherapist",
  },
  takenByOthers: {
    label: "ADMIN_HOME_TAB_TAKEN_BY_OTHERS",
    dataKey: "takenByOthers",
  },
} as const;

const ALL_TABS = Object.keys(TAB_CONFIG) as TabState[];

function CompanyInquiries({
  readOnly = false,
  showNewInquiries = true,
  showMyInquiries = true,
  myInquiriesReadOnly = false,
}: {
  readOnly?: boolean;
  showNewInquiries?: boolean;
  showMyInquiries?: boolean;
  myInquiriesReadOnly?: boolean;
}) {
  const [activeTab, setActiveTab] = React.useState<TabState>("usersWithOutTherapist");
  const [selectedDocument, setSelectedDocument] = React.useState<MainPageInquiryRow | null>(null);
  const [selectedRowId, setSelectedRowId] = React.useState<number | null>(null);
  const [pagination, setPagination] = React.useState({ start: 1, end: ITEMS_PER_PAGE });

  const admin = useAppSelector(adminSelector);
  const { data, isLoading, error } = useGetMainPageInfoQuery(undefined, {
    skip: !admin?.companyId,
  });

  useGetStatusesQuery();

  const visibleTabs = React.useMemo(
    () =>
      ALL_TABS.filter((tab) => {
        if (tab === "myInquiries") return showMyInquiries;
        return showNewInquiries;
      }),
    [showNewInquiries, showMyInquiries]
  );

  React.useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0]);
    }
  }, [visibleTabs, activeTab]);

  const filteredData = React.useMemo(() => {
    if (!data) return [];
    const dataKey = TAB_CONFIG[activeTab].dataKey;
    return data[dataKey] ?? [];
  }, [data, activeTab]);

  const paginatedData = React.useMemo(() => {
    return filteredData.slice(pagination.start - 1, pagination.end);
  }, [filteredData, pagination]);

  const handleViewDocument = (item: MainPageInquiryRow) => {
    setSelectedDocument(item);
  };

  const handleCloseDocument = () => {
    setSelectedDocument(null);
  };

  const handleRowClick = (id: number) => {
    setSelectedRowId(id);
  };

  const isRowSelected = (item: MainPageInquiryRow) => {
    return item.num === selectedRowId || item.num === selectedDocument?.num;
  };

  const displayName = (item: MainPageInquiryRow) =>
    item.fullName?.trim() ||
    [item.firstName, item.lastName].filter(Boolean).join(" ").trim() ||
    item.usersName ||
    "—";

  const therapistCell = (item: MainPageInquiryRow) => {
    const nameTTaken = (item as TTakenByOthers).therapistName;
    return nameTTaken?.trim() || "—";
  };

  if (isLoading) {
    return (
      <section className={`section ${styles.section}`}>
        <Loader />
      </section>
    );
  }
  if (error) {
    return (
      <section className={`section ${styles.section}`}>
        <div />
      </section>
    );
  }

  return (
    <section className={`section ${styles.section}`}>
      {selectedDocument && (
        <InquiryDocument
          inquiry={selectedDocument}
          openDocument={true}
          onClose={handleCloseDocument}
          readOnly={activeTab === "myInquiries" ? myInquiriesReadOnly : readOnly}
        />
      )}
      <article className="section">
        <div className={styles.headerWithToggles}>
          <h4 className={styles.home__sectionTitle}>{t("ADMIN_HOME_INQUIRIES")}</h4>

          <div className={styles.sideTogglesContainer}>
            {visibleTabs.map((tab) => (
              <button
                key={tab}
                className={`${styles.sideToggle} ${activeTab === tab ? styles.activeSideToggle : ""}`}
                type="button"
                onClick={() => setActiveTab(tab)}
              >
                <span className={styles.sideToggleText}>{t(TAB_CONFIG[tab].label)}</span>
                <span className={styles.sideToggleCount}>
                  {data?.[TAB_CONFIG[tab].dataKey]?.length ?? 0}
                </span>
              </button>
            ))}
          </div>

          <ExportTableButtons
            entity="CompanyInquiries"
            dataRows={filteredData.map((item) => ({
              InquiryId: item.num,
              Date: getPrintDate(new Date(item.timestamp)),
              ClientName: displayName(item),
              Type: item.questionnaireCompaniesType ?? "—",
              Cycle: item.cycle,
              Therapist: therapistCell(item),
              Tab: t(TAB_CONFIG[activeTab].label),
            }))}
          />

          <ul className="list">
            {filteredData.length > 0 ? (
              <li className={`listRow ${styles.listRow} ${stylesCompanyInquiries.listRowInquiries}`}>
                <p className="listCell">
                  <span className={styles.hideOnMobile}>{t("COLUMN_ID")}</span>
                  {/* <span className={styles.showOnMobile}>{t("COLUMN_ID")}</span> */}
                </p>
                <p className="listCell">{t("INQUIRY_DATE_HEADER")}</p>
                <p className="listCell">
                  <span className={styles.hideOnMobile}>{t("SHEM_MASH")}</span>
                  <span className={styles.showOnMobile}>{t("NAME_2")}</span>
                </p>
                <p className="listCell">{t("TYPE")}</p>
                <p className="listCell">{t("CYCLE")}</p>
                <p className="listCell">{t("THERAPIST_NAME")}</p>
              </li>
            ) : (
              <p className={styles.text}>{t("NO_DATA_TO_DISPLAY")}</p>
            )}

            {paginatedData.map((item, idx) => (
              <li
                key={item.num}
                className={`listRow ${styles.listRow} ${stylesCompanyInquiries.listRowInquiries} ${
                  idx % 2 === 0 ? "listRow_colored" : ""
                } ${isRowSelected(item) ? "listRow_selected" : ""}`}
                onClick={() => handleRowClick(item.num)}
              >
                <p className="listCell">{item.num}</p>
                <p className="listCell">{getPrintDate(new Date(item.timestamp))}</p>
                <p className="listCell">{displayName(item)}</p>
                <p className="listCell">{item.questionnaireCompaniesType || ""}</p>
                <p className="listCell">{item.cycle}</p>
                <p className="listCell">{therapistCell(item)}</p>
                <button
                    className={`listCell ${styles.view_btn} ${styles.btn_take}`}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDocument(item);
                    }}
                  >
                    <img src={eyeIcon} alt="View Details icon" className={styles.view_icon} />
                    {t('VIEW_DETAILS')}
                  </button>
              </li>
            ))}
          </ul>
        </div>

        {filteredData.length > 0 && (
          <div className={styles.paginationContainer}>
            <ListPagination
              limits={pagination}
              total={filteredData.length}
              setLimits={setPagination}
              step={ITEMS_PER_PAGE}
            />
          </div>
        )}
      </article>
    </section>
  );
}

export default CompanyInquiries;
